/**
 * Concept Relationship Graph View
 * 
 * Read-only visualization surface for the concept-relationship graph.
 * 
 * Constraints:
 * - proofOnly = true
 * - executionAuthority = false
 * - advisoryOnly = true
 * - No graph mutation
 * - No relationship creation
 * - No merge, rename, delete, admit, promote, resolve, auto-fix controls
 * - No drag-to-edit semantics
 * - No auto-layout that implies authority or correctness beyond advisory visualization
 */

import React, { useState, useMemo } from 'react';
import {
  ConceptRelationshipGraphAdapter,
  GraphVisualizationModel,
  GraphNode,
  GraphEdge,
} from './conceptRelationshipGraphAdapter';

interface ConceptRelationshipGraphViewProps {
  graphPath?: string;
}

export const ConceptRelationshipGraphView: React.FC<ConceptRelationshipGraphViewProps> = ({
  graphPath,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedConfidence, setSelectedConfidence] = useState<string>('all');
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<GraphEdge | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['summary', 'categories', 'confidence']));

  const adapter = useMemo(() => new ConceptRelationshipGraphAdapter(graphPath), [graphPath]);
  const graph = useMemo(() => adapter.loadAndAdapt(), [adapter]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const filteredNodes = useMemo(() => {
    return graph.nodes.filter(node => {
      if (searchQuery && !node.label.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [graph.nodes, searchQuery]);

  const filteredEdges = useMemo(() => {
    return graph.edges.filter(edge => {
      if (selectedCategory !== 'all' && edge.category !== selectedCategory) {
        return false;
      }
      if (selectedConfidence !== 'all' && edge.confidence !== selectedConfidence) {
        return false;
      }
      return true;
    });
  }, [graph.edges, selectedCategory, selectedConfidence]);

  const filteredNodeCount = filteredNodes.length;
  const filteredEdgeCount = filteredEdges.length;

  return (
    <div className="max-w-7xl mx-auto p-8 bg-[#0a0a0f] text-white">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Concept Relationship Graph</h1>
        <p className="text-gray-400">Read-only visualization of concept connections</p>
      </div>

      {/* Safety Boundaries */}
      <div className="mb-8 bg-[#1a1a2e] border border-[#f59e0b]/30 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4 text-[#f59e0b]">Safety Boundaries</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-[#0a0a0f]/50 rounded-lg p-3">
            <div className="text-[#00d4aa] font-semibold">proofOnly</div>
            <div className="text-gray-300">{graph.safetyBoundaries.proofOnly ? 'true' : 'false'}</div>
          </div>
          <div className="bg-[#0a0a0f]/50 rounded-lg p-3">
            <div className="text-[#00d4aa] font-semibold">executionAuthority</div>
            <div className="text-gray-300">{graph.safetyBoundaries.executionAuthority ? 'true' : 'false'}</div>
          </div>
          <div className="bg-[#0a0a0f]/50 rounded-lg p-3">
            <div className="text-[#00d4aa] font-semibold">advisoryOnly</div>
            <div className="text-gray-300">{graph.safetyBoundaries.advisoryOnly ? 'true' : 'false'}</div>
          </div>
          <div className="bg-[#0a0a0f]/50 rounded-lg p-3">
            <div className="text-[#00d4aa] font-semibold">noGraphMutation</div>
            <div className="text-gray-300">{graph.safetyBoundaries.noGraphMutation ? 'true' : 'false'}</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search labels..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#00d4aa]/50"
        />
      </div>

      {/* Graph Summary */}
      <div className="mb-6 bg-[#1a1a2e] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => toggleSection('summary')}
          className="w-full flex justify-between items-center mb-4"
        >
          <h2 className="text-xl font-semibold">Graph Summary</h2>
          <span className="text-gray-400">{expandedSections.has('summary') ? '▼' : '▶'}</span>
        </button>
        {expandedSections.has('summary') && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#0a0a0f]/50 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Total Nodes</div>
              <div className="text-2xl font-bold text-[#00d4aa]">{graph.summary.nodeCount}</div>
            </div>
            <div className="bg-[#0a0a0f]/50 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Total Edges</div>
              <div className="text-2xl font-bold text-[#00d4aa]">{graph.summary.edgeCount}</div>
            </div>
            <div className="bg-[#0a0a0f]/50 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Unresolved Edges</div>
              <div className="text-2xl font-bold text-[#f59e0b]">{graph.summary.unresolvedEdgeCount}</div>
            </div>
            <div className="bg-[#0a0a0f]/50 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">High Confidence</div>
              <div className="text-2xl font-bold text-[#00d4aa]">{graph.summary.highConfidenceEdges}</div>
            </div>
          </div>
        )}
      </div>

      {/* Relationship Categories */}
      <div className="mb-6 bg-[#1a1a2e] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => toggleSection('categories')}
          className="w-full flex justify-between items-center mb-4"
        >
          <h2 className="text-xl font-semibold">Relationship Categories</h2>
          <span className="text-gray-400">{expandedSections.has('categories') ? '▼' : '▶'}</span>
        </button>
        {expandedSections.has('categories') && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-gray-400">Filter by category:</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-[#0a0a0f]/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#00d4aa]/50"
              >
                <option value="all">All Categories</option>
                {graph.categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {graph.categories.map(category => (
                <div
                  key={category}
                  className={`bg-[#0a0a0f]/50 rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedCategory === category ? 'border-2 border-[#00d4aa]' : 'border border-white/10'
                  }`}
                  onClick={() => setSelectedCategory(category === selectedCategory ? 'all' : category)}
                >
                  <div className="text-white font-medium capitalize">{category}</div>
                  <div className="text-gray-400 text-sm mt-1">
                    {graph.edges.filter(e => e.category === category).length} edges
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Confidence Summary */}
      <div className="mb-6 bg-[#1a1a2e] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => toggleSection('confidence')}
          className="w-full flex justify-between items-center mb-4"
        >
          <h2 className="text-xl font-semibold">Confidence Summary</h2>
          <span className="text-gray-400">{expandedSections.has('confidence') ? '▼' : '▶'}</span>
        </button>
        {expandedSections.has('confidence') && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-gray-400">Filter by confidence:</label>
              <select
                value={selectedConfidence}
                onChange={(e) => setSelectedConfidence(e.target.value)}
                className="bg-[#0a0a0f]/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#00d4aa]/50"
              >
                <option value="all">All Confidence Levels</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#0a0a0f]/50 rounded-lg p-4">
                <div className="text-[#00d4aa] text-2xl font-bold">{graph.confidenceDistribution.high}</div>
                <div className="text-gray-400 text-sm">High Confidence</div>
              </div>
              <div className="bg-[#0a0a0f]/50 rounded-lg p-4">
                <div className="text-[#f59e0b] text-2xl font-bold">{graph.confidenceDistribution.medium}</div>
                <div className="text-gray-400 text-sm">Medium Confidence</div>
              </div>
              <div className="bg-[#0a0a0f]/50 rounded-lg p-4">
                <div className="text-[#ef4444] text-2xl font-bold">{graph.confidenceDistribution.low}</div>
                <div className="text-gray-400 text-sm">Low Confidence</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nodes */}
      <div className="mb-6 bg-[#1a1a2e] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => toggleSection('nodes')}
          className="w-full flex justify-between items-center mb-4"
        >
          <h2 className="text-xl font-semibold">Nodes ({filteredNodeCount})</h2>
          <span className="text-gray-400">{expandedSections.has('nodes') ? '▼' : '▶'}</span>
        </button>
        {expandedSections.has('nodes') && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredNodes.slice(0, 20).map(node => (
              <div
                key={node.nodeId}
                className={`bg-[#0a0a0f]/50 rounded-lg p-3 cursor-pointer transition-colors ${
                  selectedNode?.nodeId === node.nodeId ? 'border-2 border-[#00d4aa]' : 'border border-white/10'
                }`}
                onClick={() => setSelectedNode(selectedNode?.nodeId === node.nodeId ? null : node)}
              >
                <div className="text-white font-medium">{node.label}</div>
                <div className="text-gray-400 text-sm mt-1">
                  {node.nodeType} • {node.confidence}
                </div>
              </div>
            ))}
            {filteredNodeCount > 20 && (
              <div className="text-gray-400 text-center py-2">
                Showing 20 of {filteredNodeCount} nodes
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Node Details */}
      {selectedNode && (
        <div className="mb-6 bg-[#1a1a2e] border border-[#00d4aa]/30 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4 text-[#00d4aa]">Selected Node Details</h2>
          <div className="space-y-3">
            <div>
              <div className="text-gray-400 text-sm">Label</div>
              <div className="text-white">{selectedNode.label}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Node Type</div>
              <div className="text-white">{selectedNode.nodeType}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Concept ID</div>
              <div className="text-white">{selectedNode.conceptId || 'N/A'}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Confidence</div>
              <div className="text-white">{selectedNode.confidence}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Source Artifacts</div>
              <div className="text-white text-sm">{selectedNode.sourceArtifacts.length} sources</div>
            </div>
          </div>
        </div>
      )}

      {/* Edges */}
      <div className="mb-6 bg-[#1a1a2e] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => toggleSection('edges')}
          className="w-full flex justify-between items-center mb-4"
        >
          <h2 className="text-xl font-semibold">Edges ({filteredEdgeCount})</h2>
          <span className="text-gray-400">{expandedSections.has('edges') ? '▼' : '▶'}</span>
        </button>
        {expandedSections.has('edges') && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredEdges.map(edge => (
              <div
                key={edge.edgeId}
                className={`bg-[#0a0a0f]/50 rounded-lg p-3 cursor-pointer transition-colors ${
                  selectedEdge?.edgeId === edge.edgeId ? 'border-2 border-[#00d4aa]' : 'border border-white/10'
                }`}
                onClick={() => setSelectedEdge(selectedEdge?.edgeId === edge.edgeId ? null : edge)}
              >
                <div className="text-white font-medium">
                  {graph.nodes.find(n => n.nodeId === edge.sourceNodeId)?.label || edge.sourceNodeId}
                  {' '}
                  <span className="text-[#f59e0b]">{edge.relationshipLabel}</span>
                  {' '}
                  {graph.nodes.find(n => n.nodeId === edge.targetNodeId)?.label || edge.targetNodeId}
                </div>
                <div className="text-gray-400 text-sm mt-1">
                  {edge.category} • {edge.confidence}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Edge Details */}
      {selectedEdge && (
        <div className="mb-6 bg-[#1a1a2e] border border-[#00d4aa]/30 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4 text-[#00d4aa]">Selected Edge Details</h2>
          <div className="space-y-3">
            <div>
              <div className="text-gray-400 text-sm">Relationship Type</div>
              <div className="text-white">{selectedEdge.relationshipLabel}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Category</div>
              <div className="text-white capitalize">{selectedEdge.category}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Directional</div>
              <div className="text-white">{selectedEdge.directional ? 'Yes' : 'No'}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Confidence</div>
              <div className="text-white">{selectedEdge.confidence}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Evidence Sources</div>
              <div className="text-white text-sm">{selectedEdge.evidenceSources.length} sources</div>
            </div>
          </div>
        </div>
      )}

      {/* Unresolved Edges */}
      <div className="mb-6 bg-[#1a1a2e] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => toggleSection('unresolved')}
          className="w-full flex justify-between items-center mb-4"
        >
          <h2 className="text-xl font-semibold">Unresolved Edges ({graph.unresolvedEdges.length})</h2>
          <span className="text-gray-400">{expandedSections.has('unresolved') ? '▼' : '▶'}</span>
        </button>
        {expandedSections.has('unresolved') && (
          <div className="space-y-2">
            {graph.unresolvedEdges.length === 0 ? (
              <div className="text-gray-400 text-center py-4">No unresolved edges</div>
            ) : (
              graph.unresolvedEdges.map(edge => (
                <div key={edge.edgeId} className="bg-[#0a0a0f]/50 rounded-lg p-3 border border-[#f59e0b]/30">
                  <div className="text-white font-medium">
                    {edge.sourceLabel} → {edge.relationshipLabel} → {edge.targetLabel}
                  </div>
                  <div className="text-[#f59e0b] text-sm mt-1">⚠ {edge.reason}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Evidence Sources */}
      <div className="mb-6 bg-[#1a1a2e] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => toggleSection('evidence')}
          className="w-full flex justify-between items-center mb-4"
        >
          <h2 className="text-xl font-semibold">Evidence Sources ({graph.evidenceSources.length})</h2>
          <span className="text-gray-400">{expandedSections.has('evidence') ? '▼' : '▶'}</span>
        </button>
        {expandedSections.has('evidence') && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {graph.evidenceSources.map(source => (
              <div key={source} className="bg-[#0a0a0f]/50 rounded-lg p-3">
                <div className="text-white text-sm font-mono">{source}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generated At */}
      <div className="text-gray-400 text-sm text-center">
        Generated at {new Date(graph.generatedAt).toLocaleString()}
      </div>
    </div>
  );
};
